import React, { useCallback, useEffect, useState } from 'react'
import { useChannelStateContext } from 'stream-chat-react'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import api from 'services/api'
import useToast from 'hooks/useToast'

// Whether the reader is told in Slack when this thread's user writes: the
// state, a toggle, and the dialog that offers to connect Slack first. The
// desktop header shows it as a bell, a phone's profile as a switch.
export const useSlackSubscription = (channel) => {
  const toast = useToast()
  const channelCid = channel?.cid
  // Subscriptions only make sense for Team Reclip channels; the bell is hidden
  // elsewhere (and the API rejects non-TR cids as defense-in-depth).
  const isTeamReclipChannel = channel?.data?.is_team_reclip_channel === true

  const [loading, setLoading] = useState(true)
  const [linked, setLinked] = useState(false)
  const [subscribed, setSubscribed] = useState(false)
  const [connectOpen, setConnectOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const [status, subscriptions] = await Promise.all([
          api.slack.getStatus(),
          api.slack.getSubscriptions(),
        ])
        if (cancelled) return
        setLinked(status.linked)
        setSubscribed(subscriptions.includes(channelCid))
      } catch (err) {
        if (!cancelled) toast({ message: err.message, severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (channelCid && isTeamReclipChannel) load()
    return () => {
      cancelled = true
    }
  }, [channelCid, isTeamReclipChannel, toast])

  const startConnect = useCallback(async () => {
    try {
      const { authorize_url } = await api.slack.authorize(channelCid)
      window.location.assign(authorize_url)
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    }
  }, [channelCid, toast])

  const toggleSubscription = useCallback(async () => {
    if (!linked) {
      setConnectOpen(true)
      return
    }
    setLoading(true)
    try {
      if (subscribed) {
        await api.slack.unsubscribe(channelCid)
        setSubscribed(false)
        toast({ message: 'Unsubscribed from Slack notifications.' })
      } else {
        await api.slack.subscribe(channelCid)
        setSubscribed(true)
        toast({ message: 'Subscribed to Slack notifications.' })
      }
    } catch (err) {
      toast({ message: err.message, severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [linked, subscribed, channelCid, toast])

  return {
    available: Boolean(channelCid && isTeamReclipChannel),
    loading,
    subscribed,
    toggle: toggleSubscription,
    connectDialog: (
      <Dialog open={connectOpen} onClose={() => setConnectOpen(false)}>
        <DialogTitle>Connect Slack</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To get notified in Slack when this user messages Team Reclip,
            connect your Slack account. You'll be subscribed to this
            conversation once connected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={startConnect}>
            Connect with Slack
          </Button>
        </DialogActions>
      </Dialog>
    ),
  }
}

const SubscribeButton = () => {
  const { channel } = useChannelStateContext()
  const { available, loading, subscribed, toggle, connectDialog } =
    useSlackSubscription(channel)

  if (!available) return null

  return (
    <>
      <IconButton
        title={
          subscribed
            ? 'Unsubscribe from Slack notifications'
            : 'Subscribe to Slack notifications'
        }
        sx={{ marginLeft: 2 }}
        disabled={loading}
        onClick={toggle}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : subscribed ? (
          <NotificationsIcon color="primary" />
        ) : (
          <NotificationsNoneIcon />
        )}
      </IconButton>
      {connectDialog}
    </>
  )
}

export default SubscribeButton
